import React, { lazy } from "react";
import { Route, Routes, Navigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { lazyRetry, getFeatureToggle } from "../components/common/helpers";
import { PrivateRoute } from "../components/common/providers";
import { getTokenCookie } from "../components/common/helpers/auth-header";
import ScreensError from "./error";
import { UIWidgetsDiv } from "../components/common/ui/widgets/division/Division";
import { USER_TYPE_OBJ_CONSTANT } from "../components/team/constants";
const ScreensUserLogin = lazy(() => lazyRetry(() => import("./user/login")));
const ScreensUserRegistration = lazy(() => lazyRetry(() => import("./user/registration")));
const ScreensHome = lazy(() => lazyRetry(() => import("./home")));
const ScreensHomeSignIn = lazy(() => lazyRetry(() => import("./home/signIn")));
const ScreensHomeSignUp = lazy(() => lazyRetry(() => import("./home/signUp")));
const ScreensHomeRegistrationSuccess = lazy(() => lazyRetry(() => import("./home/signUp/registrationSuccess")));
const Status = lazy(() => lazyRetry(() => import("./status")));
const ScreensInviteSuccess = lazy(() => lazyRetry(() => import("./home/invite/accept/success")));
const ScreensAdminCertification = lazy(() => lazyRetry(() => import("./adminPortal/certification")));
const ScreensAdminSerialNumber = lazy(() => lazyRetry(() => import("./adminPortal/serialNumber")));
const ScreensAdminDashboard = lazy(() => lazyRetry(() => import("./adminPortal/dashboard")));
const ScreensAdminPortalLogin = lazy(() => lazyRetry(() => import("./adminPortal/login")));
const ScreensTeamInvite = lazy(() => lazyRetry(() => import("./teamInvite")));
const ScreensAssetHome = lazy(() => lazyRetry(() => import("./assets/home")));
const ScreensMeasurementList = lazy(() =>
  lazyRetry(() => import("./measurements/list")),
);
const ScreensMeasurementDetail = lazy(() =>
  lazyRetry(() => import("./measurements/detail")),
);
const ScreensAssetDashboard = lazy(() =>
  lazyRetry(() => import("./dashboard/asset")),
);
const ScreensTeam = lazy(() => lazyRetry(() => import("./team")));
const ScreensReports = lazy(() => lazyRetry(() => import("./reports")));
const ScreensExport = lazy(() => lazyRetry(() => import("./export")));
const ScreensWOHome = lazy(() => lazyRetry(() => import("./workOrders/home")));
const ScreensSessionDetail = lazy(() =>
  lazyRetry(() => import("./sessiondetail")),
);
const ScreensSessions = lazy(() => lazyRetry(() => import("./sessions")));
const ScreensAssetSeverity = lazy(() =>
  lazyRetry(() => import("./assets/severity")),
);
const ScreensAssetSeverityDetail = lazy(() =>
  lazyRetry(() => import("./assets/severity/detail")),
);
const ScreensAssetAnalysis = lazy(() =>
  lazyRetry(() => import("./assets/analysis")),
);
const ScreensAssetSavedData = lazy(() =>
  lazyRetry(() => import("./assets/savedData")),
);
const ScreensAssetFeeds = lazy(() => lazyRetry(() => import("./assets/feeds")));
const ScreensAssetStatus = lazy(() =>
  lazyRetry(() => import("./assets/status")),
);
const ScreensAddAssetHome = lazy(() =>
  lazyRetry(() => import("./assets/home/add")),
);
const ScreensAssetInfo = lazy(() =>
  lazyRetry(() => import("./assets/home/info")),
);
const ScreensAssetAlarms = lazy(() =>
  lazyRetry(() => import("./assets/alarms")),
);
const ScreensTeamMemberDetail = lazy(() =>
  lazyRetry(() => import("./teamMemberDetail")),
);
const ScreensTeamSubscriptionRequest = lazy(() =>
  lazyRetry(() => import("./team/subscription")),
);
const ScreensTeamProductLicenses = lazy(() =>
  lazyRetry(() => import("./team/productLicenses")),
);
const ScreensExportAddMeasurements = lazy(() =>
  lazyRetry(() => import("./export/addMeasurements")),
);
const ScreensAssignAssetWO = lazy(() =>
  lazyRetry(() => import("./assets/home/assign/WorkOrder")),
);
const ScreensAssignMeas = lazy(() =>
  lazyRetry(() => import("./assets/home/assign/Measurement")),
);
const ScreenCreateReport = lazy(() =>
  lazyRetry(() => import("./reports/create/index")),
);
const ScreensAcceptInvite = lazy(() =>
  lazyRetry(() => import("./home/invite/accept")),
);
const ScreenDeclineInvite = lazy(() =>
  lazyRetry(() => import("./home/invite/decline")),
);
const ScreensSettingPage = lazy(() =>
  lazyRetry(() => import("./home/settingPage")),
);
const ScreensDataPrivacy = lazy(() =>
  lazyRetry(() => import("./home/dataPrivacy")),
);
const ScreensAddWO = lazy(() => lazyRetry(() => import("./workOrders/add")));
const ScreensWOLatestUpdate = lazy(() =>
  lazyRetry(() => import("./workOrders/latestUpdate")),
);
const WorkOrderSavedData = lazy(() =>
  lazyRetry(() => import("./workOrders/savedData")),
);
const ScreensEula = lazy(() => lazyRetry(() => import("./home/common/eula")));
const ScreensPremiumServices = lazy(() =>
  lazyRetry(() => import("./home/common/premium_services")),
);
const ScreensPrivacyPolicy = lazy(() =>
  lazyRetry(() => import("./home/common/privacy_policy")),
);
const ScreensHomeForgotPassword = lazy(() =>
  lazyRetry(() => import("./home/forgotPassword")),
);
const ScreensForgotReset = lazy(() =>
  lazyRetry(() => import("./home/resetPassword")),
);
const ScreenSOPs = lazy(() => lazyRetry(() => import("./sops/create/index")));

const ScreensEditSOPs = lazy(() =>
  lazyRetry(() => import("./sops/edit/index")),
);

const DataPrivacyRoute = ({ component: Component }) => {
  const { user } = useSelector((state) => state.login);
  const userToken = getTokenCookie();
  const { cc } = useParams();
  const location = useLocation();
  const currentUser =
    (Array.isArray(user?.user) && user.user[0]) ||
    (user?.user && !Array.isArray(user.user) ? user.user : null) ||
    null;
  const isAdminUser =
    currentUser?.roleId === USER_TYPE_OBJ_CONSTANT.ADMINISTRATOR[1] ||
    currentUser?.roleId === USER_TYPE_OBJ_CONSTANT.INDIVIDUAL_USER[1];
  const isEuDataActEnabled = getFeatureToggle("EU Data Act")[0]?.isVisible ?? false;

  if (!user && !userToken) {
    return <PrivateRoute component={Component} />;
  }

  if (!isEuDataActEnabled || !isAdminUser) {
    return (
      <Navigate
        to={cc ? `/${cc}/fc/dashboard` : "/"}
        state={{ from: location }}
        replace
      />
    );
  }
  return <PrivateRoute component={Component} />;
};

const SopPrivateRoute = ({ component: Component }) => {
  const sopAccessAuthorized = useSelector((state) => state.login.sopAccessAuthorized);
  const { cc } = useParams();
  const location = useLocation();
  // null means persistSopAccess hasn't resolved yet — don't redirect prematurely
  if (sopAccessAuthorized === null) {
    return <UIWidgetsDiv loading />;
  }
  if (!sopAccessAuthorized) {
    return (
      <Navigate
        to={cc ? `/${cc}/fc/dashboard` : "/"}
        state={{ from: location }}
        replace
      />
    );
  }
  return <PrivateRoute component={Component} />;
};

const ScreensRoutes = () => {
  return (
    <React.Suspense fallback={<UIWidgetsDiv loading />}>
      <Routes>
          <Route path="/:cc/team" element={<PrivateRoute component={ScreensTeam} />} />
          <Route path="/:cc/settings" element={<PrivateRoute component={ScreensSettingPage} />} />
          <Route path="/:cc/data-privacy" element={<DataPrivacyRoute component={ScreensDataPrivacy} />} />
          <Route path="/:cc/asset-severity/dashboard" element={<PrivateRoute component={ScreensAssetSeverity} />} />
          <Route path="/:cc/work-orders/assign/workorderadd/:workOrderId" element={<PrivateRoute component={ScreensAssignAssetWO} />} />
          <Route path="/:cc/measurements/assign/:id/meas" element={<PrivateRoute component={ScreensAssignMeas} />} />
          <Route path="/:cc/work-orders/assignWO/:id/meas" element={<PrivateRoute component={ScreensWOHome} />} />
          <Route path="/:cc/work-orders/assignWO/measSelectAll" element={<PrivateRoute component={ScreensWOHome} />} />
          <Route path="/:cc/work-orders/assignWO/:id/measDetail" element={<PrivateRoute component={ScreensWOHome} />} />
          <Route path="/:cc/measurements/assign/:id/measDetail" element={<PrivateRoute component={ScreensAssignMeas} />} />
          <Route path="/:cc/measurements/assign/measSelectAll" element={<PrivateRoute component={ScreensAssignMeas} />} />
          <Route path="/:cc/asset-severity-detail/:assetId" element={<PrivateRoute component={ScreensAssetSeverityDetail} />} />
          <Route path="/:cc/remote-monitoring/:model" element={<PrivateRoute component={ScreensSessions} />} />
          <Route path="/:cc/saved-session/:model" element={<PrivateRoute component={ScreensSessions} />} />
          <Route path="/:cc/powermonitor/remote-monitoring-detail/:id" element={<PrivateRoute component={ScreensSessionDetail} />} />
          <Route path="/:cc/pqlogger/remote-monitoring-detail/:id" element={<PrivateRoute component={ScreensSessionDetail} />} />
          <Route path="/:cc/vibration/remote-monitoring-detail/:id" element={<PrivateRoute component={ScreensSessionDetail} />} />
          <Route path="/:cc/measurements/detail/:id" element={<PrivateRoute component={ScreensMeasurementDetail} />} />
          <Route path="/:cc/measurements" element={<PrivateRoute component={ScreensMeasurementList} />} />
          <Route path="/:cc/asset-dashboard/:containerId/:assetId/analysis" element={<PrivateRoute component={ScreensAssetAnalysis} />} />
          <Route path="/:cc/asset-dashboard/:containerId/:assetId/savedData" element={<PrivateRoute component={ScreensAssetSavedData} />} />
          <Route path="/:cc/asset-dashboard/:containerId/:assetId/newsFeed" element={<PrivateRoute component={ScreensAssetFeeds} />} />
          <Route path="/:cc/asset-dashboard/:containerId/:assetId/status" element={<PrivateRoute component={ScreensAssetStatus} />} />
          <Route path="/:cc/asset-dashboard/:containerId/:assetId/alarm" element={<PrivateRoute component={ScreensAssetAlarms} />} />
          <Route path="/:cc/exports/list" element={<PrivateRoute component={ScreensExport} />} />
          <Route path="/:cc/exports/addMeasurements" element={<PrivateRoute component={ScreensExportAddMeasurements} />} />
          <Route path="/:cc/home" element={<ScreensHome />} />
          <Route path="/:cc/stores/home" element={<ScreensHome />} />
          <Route path="/:cc/stores/login" element={<ScreensHomeSignIn />} />
          <Route path="/:cc/stores/registration" element={<ScreensHomeSignUp />} />
          <Route path="/:cc/stores/registration_success" element={<ScreensHomeRegistrationSuccess />} />
          <Route path="/:cc/eula" element={<ScreensEula />} />
          <Route path="/:cc/premium-services-agreement" element={<ScreensPremiumServices />} />
          <Route path="/:cc/privacy-policy" element={<ScreensPrivacyPolicy />} />
          <Route path="/login" element={<ScreensUserLogin />} />
          <Route path="/register" element={<ScreensUserRegistration />} />
          <Route path="/password/forgot" element={<ScreensHomeForgotPassword />} />
          <Route path="/:cc/password/forgot" element={<ScreensHomeForgotPassword />} />
          <Route path="/password/:resetId" element={<ScreensForgotReset />} />
          <Route path="/:cc/password/:resetId" element={<ScreensForgotReset />} />
          <Route path="/invites/:id/join" element={<ScreensAcceptInvite />} />
          <Route path="/invites/:id/decline" element={<ScreenDeclineInvite />} />
          <Route path="/:cc/team/edit/:id" element={<ScreensTeamMemberDetail />} />
          <Route path="/:cc/team/invite" element={<ScreensTeamInvite />} />
          <Route path="/:cc/reports/list" element={<PrivateRoute component={ScreensReports} />} />
          <Route path="/:cc/reports/create_asset_report" element={<PrivateRoute component={ScreenCreateReport} />} />
          <Route path="/:cc/team/subscriptionRequest" element={<PrivateRoute component={ScreensTeamSubscriptionRequest} />} />
          <Route path="/:cc/team/manageLicenses/:productId" element={<PrivateRoute component={ScreensTeamProductLicenses} />} />
          <Route path="/:cc/asset-dashboard/:containerId" element={<PrivateRoute component={ScreensAssetHome} />} />
          <Route path="/:cc/asset-dashboard/:containerId/add" element={<PrivateRoute component={ScreensAddAssetHome} />} />
          <Route path="/:cc/asset-dashboard/:containerId/:assetId/info" element={<PrivateRoute component={ScreensAssetInfo} />} />
          <Route path="/:cc/work-orders/add" element={<PrivateRoute component={ScreensAddWO} />} />
          <Route path="/:cc/work-orders/edit/:workOrderId" element={<PrivateRoute component={ScreensAddWO} />} />
          <Route path="/:cc/work-orders/list" element={<PrivateRoute component={ScreensWOHome} />} />
          <Route path="/:cc/work-orders/:workOrderId/latestupdate" element={<PrivateRoute component={ScreensWOLatestUpdate} />} />
          <Route path="/:cc/work-orders/:workOrderId/savedData" element={<PrivateRoute component={WorkOrderSavedData} />} />
          <Route path="/health/status" element={<Status />} />
          <Route path="/:cc/invites/:id/join/success" element={<ScreensInviteSuccess />} />
          <Route path="/admin/login" element={<ScreensAdminPortalLogin />} />
          <Route path="/:cc/admin/login" element={<ScreensAdminPortalLogin />} />
          <Route path="/:cc/admin/dashboard" element={<ScreensAdminDashboard />} />
          <Route path="/:cc/admin/certification" element={<ScreensAdminCertification />} />
          <Route path="/:cc/admin/serialNumbers" element={<ScreensAdminSerialNumber />} />
          <Route path="/" element={<PrivateRoute component={ScreensAssetDashboard} />} />
          <Route path="/:cc/fc/dashboard" element={<PrivateRoute component={ScreensAssetDashboard} />} />
          <Route path="/:cc/sop" element={<SopPrivateRoute component={ScreenSOPs} />} />
          <Route path="/:cc/sop/edit/:id" element={<SopPrivateRoute component={ScreensEditSOPs} />} />
          <Route path="*" element={<ScreensError />} />
      </Routes>
    </React.Suspense>
  );
};

export default ScreensRoutes;
